import type { HandlingUnitSettings } from "@RequestFormV2/sections/PackingList/types";
import { getPackingListFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";

function hasDefault(value: number | null | undefined): value is number {
  return value !== null && value !== undefined;
}

type ApplyHandlingUnitDefaultsParams = {
  setValue: (name: string, value: unknown) => void;
  index: number;
  settings: HandlingUnitSettings | undefined;
};

/**
 * Applies a handling unit's configured default dimensions/weight to its row when the unit is
 * selected. The default values are set as-is (no inch/cm or kg/lbs conversion); the row's
 * `m_unit` and `w_unit` are set to the config's source units when provided. Only fields with a
 * non-null default are written, and fields whose mandatory setting is "off" are skipped.
 */
export function applyHandlingUnitDefaults({ setValue, index, settings }: ApplyHandlingUnitDefaultsParams) {
  if (!settings) return;

  if (settings.l_w_h_cbm) {
    // Match RFV1 PackingListAddItem: CBM Input HUs default scope to "piece".
    setValue(getPackingListFieldPath(index, "cbm_scope"), "piece");
    setValue(getPackingListFieldPath(index, "length"), null);
    setValue(getPackingListFieldPath(index, "width"), null);
    setValue(getPackingListFieldPath(index, "height"), null);
    // Reloaded L/W/H rows carry a computed l_w_h_cbm_total; drop it so it is not submitted as entered CBM.
    setValue(getPackingListFieldPath(index, "l_w_h_cbm_piece"), null);
    setValue(getPackingListFieldPath(index, "l_w_h_cbm_total"), null);
  } else if (settings.l_w_h !== "off") {
    if (settings.default_m_unit) {
      setValue(getPackingListFieldPath(index, "m_unit"), settings.default_m_unit);
    }
    if (hasDefault(settings.default_length)) {
      setValue(getPackingListFieldPath(index, "length"), settings.default_length);
    }
    if (hasDefault(settings.default_width)) {
      setValue(getPackingListFieldPath(index, "width"), settings.default_width);
    }
    if (hasDefault(settings.default_height)) {
      setValue(getPackingListFieldPath(index, "height"), settings.default_height);
    }
    setValue(getPackingListFieldPath(index, "cbm_scope"), null);
    setValue(getPackingListFieldPath(index, "l_w_h_cbm_piece"), null);
    setValue(getPackingListFieldPath(index, "l_w_h_cbm_total"), null);
  } else {
    setValue(getPackingListFieldPath(index, "cbm_scope"), null);
    setValue(getPackingListFieldPath(index, "l_w_h_cbm_piece"), null);
    setValue(getPackingListFieldPath(index, "l_w_h_cbm_total"), null);
  }

  if (settings.weight !== "off" && hasDefault(settings.default_weight)) {
    if (settings.default_w_unit) {
      setValue(getPackingListFieldPath(index, "w_unit"), settings.default_w_unit);
    }
    setValue(getPackingListFieldPath(index, "weight_piece"), settings.default_weight);
  }
}
